import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getContactTypeLabel, getContactTypeColor } from '../utils';
import { useAuth } from '../../../context/AuthContext';
import { formatDate } from '../../../utils/dateUtils';

export const useColumns = (contacts = []) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  return useMemo(() => [
    {
      field: 'display_name',
      headerName: 'Name',
      renderCell: (params) => (
        <Link 
          to={`/contacts/${params.id}${userId ? `?userId=${userId}` : ''}`}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          {params.display_name}
          {params.company_name && params.company_name !== params.display_name && ` (${params.company_name})`}
        </Link>
      ),
      sortable: true,
    },
    {
      field: 'contact',
      headerName: 'Contact',
      renderCell: (params) => (
        <Link 
          to={`/contacts/${params.id}${userId ? `?userId=${userId}` : ''}`}
          className="flex flex-col text-gray-600 hover:text-gray-900"
        >
          {params.email && <span>{params.email}</span>}
          {params.phone && <span>{params.phone}</span>}
        </Link>
      ),
      sortable: false,
    },
    {
      field: 'contact_type',
      headerName: 'Type',
      renderCell: (params) => {
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContactTypeColor(params.contact_type)}`}>
            {getContactTypeLabel(params.contact_type)}
          </span>
        );
      },
      sortable: true,
      align: 'center',
    },
    {
      field: 'total_transactions',
      headerName: 'Transactions',
      renderCell: (params) => params.total_transactions || '0',
      sortable: true,
      align: 'center',
      type: 'number',
    },
    {
      field: 'total_value',
      headerName: 'Sales',
      renderCell: (params) => (
        params.total_value 
          ? `$${Number(params.total_value).toLocaleString('en-US', { 
              minimumFractionDigits: 0, 
              maximumFractionDigits: 2,
              minimumSignificantDigits: 1
            })}` 
          : '-'
      ),
      sortable: true,
      align: 'right',
      type: 'number',
      footer: {
        label: 'Total',
        getValue: (rows) => rows.reduce((sum, row) => sum + (Number(row.total_value) || 0), 0),
        render: (value) => `$${value.toLocaleString('en-US', { 
          minimumFractionDigits: 0, 
          maximumFractionDigits: 2,
          minimumSignificantDigits: 1
        })}`
      },
    },
    {
      field: 'total_purchases',
      headerName: 'Purchases',
      renderCell: (params) => (
        params.total_purchases 
          ? `$${Number(params.total_purchases).toLocaleString('en-US', { 
              minimumFractionDigits: 0, 
              maximumFractionDigits: 2,
              minimumSignificantDigits: 1
            })}` 
          : '-'
      ),
      sortable: true,
      align: 'right',
      type: 'number',
      footer: {
        label: 'Total',
        getValue: (rows) => rows.reduce((sum, row) => sum + (Number(row.total_purchases) || 0), 0),
        render: (value) => `$${value.toLocaleString('en-US', { 
          minimumFractionDigits: 0, 
          maximumFractionDigits: 2,
          minimumSignificantDigits: 1
        })}`
      },
    },
    {
      field: 'last_transaction_date',
      headerName: 'Last Activity',
      renderCell: (params) => (
        params.last_transaction_date 
          ? formatDate(params.last_transaction_date, { user })
          : '-'
      ),
      sortable: true,
      align: 'center',
      type: 'date',
    },
    {
      field: 'notes',
      headerName: 'Notes',
      renderCell: (params) => params.notes || '-',
      sortable: false,
    },
  ], [userId]);
};
